import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { ArrowDownLeft, ArrowUpRight, Search, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';
import { DateRangePicker } from '@/components/date-range-picker';
import { Combobox } from '@/components/combobox';
import { useStore } from '@/lib/store-context';
import {
  getMasters, getTransfers,
  type TransferDirection,
} from '@/lib/inventory-api';
import { fmtINR, fmtNumber, fmtDate } from '@/lib/format';
import { cn } from '@/lib/utils';

interface StoreOpt { store: string; label: string }
async function fetchStores(): Promise<StoreOpt[]> {
  const r = await fetch('/api/stores', { credentials: 'include' });
  if (!r.ok) return [];
  const j = (await r.json()) as { stores: StoreOpt[] };
  return j.stores ?? [];
}

const Search$ = z.object({
  from: z.string().optional(),
  to: z.string().optional(),
  direction: z.enum(['all', 'in', 'out']).optional(),
  style: z.string().optional(),
  color: z.string().optional(),
  subgroup: z.string().optional(),
  item: z.string().optional(),
  q: z.string().optional(),
  page: z.coerce.number().int().min(1).optional(),
});

export const Route = createFileRoute('/_app/inventory/transfers')({
  validateSearch: Search$,
  component: Page,
});

const PAGE_SIZE = 100;

function Page() {
  const search = Route.useSearch();
  const nav = Route.useNavigate();
  const { selected: store } = useStore();

  // Default date window: last 30 days.
  const today = isoToday();
  const monthBack = isoAdd(today, -30);
  const from = search.from ?? monthBack;
  const to = search.to ?? today;
  const direction = (search.direction ?? 'all') as TransferDirection;
  const style = search.style ?? null;
  const color = search.color ?? null;
  const subgroup = search.subgroup ?? null;
  const item = search.item ?? null;
  const q = search.q ?? null;
  const page = search.page ?? 1;

  const [draft, setDraft] = React.useState(q ?? '');
  React.useEffect(() => setDraft(q ?? ''), [q]);
  React.useEffect(() => {
    const t = setTimeout(() => {
      const next = draft.trim();
      if (next !== (q ?? '')) {
        nav({ to: '.', search: (prev) => ({ ...prev, q: next || undefined, page: undefined }), replace: true });
      }
    }, 250);
    return () => clearTimeout(t);
  }, [draft, q, nav]);

  const setF = (patch: Record<string, unknown>) => {
    nav({
      to: '.',
      search: (prev) => {
        const next = { ...(prev as Record<string, unknown>) };
        for (const [k, v] of Object.entries(patch)) {
          if (v === null || v === '' || v === undefined) delete next[k];
          else next[k] = v;
        }
        // Filter changes reset to page 1 so users don't get empty later pages.
        if (Object.keys(patch).some((k) => k !== 'page')) delete next.page;
        return next;
      },
      replace: true,
    });
  };

  const mastersQ = useQuery({
    queryKey: ['inventory:masters'],
    queryFn: getMasters,
    staleTime: 5 * 60_000,
  });
  const storesQ = useQuery({
    queryKey: ['stores'],
    queryFn: fetchStores,
    staleTime: 5 * 60_000,
  });
  const transfersQ = useQuery({
    queryKey: ['inventory:transfers', from, to, direction, style, color, subgroup, item, q, page, store],
    queryFn: () => getTransfers({
      from, to, direction,
      style, color, subgroup, item, q,
      store, page, limit: PAGE_SIZE,
    }),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });

  const totalPages = transfersQ.data ? Math.max(1, Math.ceil(transfersQ.data.total / PAGE_SIZE)) : 1;
  const totals = transfersQ.data?.totals;
  
  // Look up store label from db name
  const storeLabel = React.useMemo(() => {
    const dbName = transfersQ.data?.store;
    if (!dbName || !storesQ.data) return null;
    return storesQ.data.find((s) => s.store === dbName)?.label ?? dbName;
  }, [transfersQ.data?.store, storesQ.data]);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Daleep · Inventory</p>
          <h1 className="font-display text-3xl font-medium tracking-tight text-ink sm:text-4xl">Stock Transfer</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {storeLabel && <><span className="font-medium text-foreground">{storeLabel}</span> · </>}
            Sub-store transfers logged by the POS · home = {transfersQ.data?.home ?? '—'}
          </p>
        </div>
        <Button asChild variant="outline" size="sm">
          <Link to="/inventory" search={(prev) => prev}>← Stock Ledger</Link>
        </Button>
      </header>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label="Out · items" value={totals ? fmtNumber(totals.outCount) : '—'} hint={totals ? `${fmtINR(totals.outAmt)} value` : ''} accent="rose" icon={<ArrowUpRight className="h-3.5 w-3.5" />} />
        <Kpi label="In · items"  value={totals ? fmtNumber(totals.inCount)  : '—'} hint={totals ? `${fmtINR(totals.inAmt)} value`  : ''} accent="emerald" icon={<ArrowDownLeft className="h-3.5 w-3.5" />} />
        <Kpi label="Out · qty"   value={totals ? fmtNumber(totals.outQty)   : '—'} />
        <Kpi label="In · qty"    value={totals ? fmtNumber(totals.inQty)    : '—'} />
      </div>

      <Card>
        <CardContent className="space-y-3 pt-4">
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Date range</label>
              <DateRangePicker from={from} to={to} onChange={(r) => setF({ from: r.from, to: r.to })} />
            </div>
            <div>
              <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Direction</label>
              <div role="radiogroup" aria-label="Direction" className="flex h-10 items-center rounded-md border bg-background p-0.5">
                {(['all', 'in', 'out'] as const).map((d) => {
                  const selected = direction === d;
                  return (
                    <button
                      key={d}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => setF({ direction: d === 'all' ? undefined : d })}
                      className={cn(
                        'h-full rounded px-3 text-xs font-medium capitalize transition-colors',
                        selected ? 'bg-ink text-white shadow-sm' : 'text-muted-foreground hover:text-ink',
                      )}
                    >
                      {d}
                    </button>
                  );
                })}
              </div>
            </div>
            <Combobox
              label="Article"
              value={style ?? ''}
              options={mastersQ.data?.style ?? []}
              onChange={(v) => setF({ style: v || undefined })}
              placeholder="All articles"
              className="min-w-[200px]"
            />
            <Combobox
              label="Color"
              value={color ?? ''}
              options={mastersQ.data?.color ?? []}
              onChange={(v) => setF({ color: v || undefined })}
              placeholder="All colors"
              className="min-w-[160px]"
            />
            <Combobox
              label="Sub-group"
              value={subgroup ?? ''}
              options={mastersQ.data?.subgroup ?? []}
              onChange={(v) => setF({ subgroup: v || undefined })}
              placeholder="All"
              className="min-w-[140px]"
            />
            <Combobox
              label="Item"
              value={item ?? ''}
              options={mastersQ.data?.item ?? []}
              onChange={(v) => setF({ item: v || undefined })}
              placeholder="All items"
              className="min-w-[160px]"
            />
            <div className="min-w-[220px] flex-1">
              <label className="mb-1 flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                <Search className="h-3.5 w-3.5" /> Search
              </label>
              <div className="relative">
                <Input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Barcode, sticker, item, bill no…"
                  className="h-10 pr-8"
                />
                {draft && (
                  <button
                    type="button" onClick={() => setDraft('')} aria-label="Clear"
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-ink"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {transfersQ.data
              ? <>Transfers · <span className="font-mono-num">{fmtNumber(transfersQ.data.total)}</span> rows · page {page}/{totalPages}</>
              : 'Transfers'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {transfersQ.isLoading && !transfersQ.data && <p className="text-sm text-muted-foreground">Loading…</p>}
          {transfersQ.error && <p className="text-sm text-destructive">{(transfersQ.error as Error).message}</p>}
          {transfersQ.data && transfersQ.data.rows.length === 0 && (
            <p className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
              No transfers match. Try widening the date range or clearing filters.
            </p>
          )}
          {transfersQ.data && transfersQ.data.rows.length > 0 && (
            <div className={cn('overflow-x-auto', transfersQ.isFetching && 'opacity-60 transition-opacity')}>
              <Table>
                <THead>
                  <TR>
                    <TH>Store</TH>
                    <TH>Date</TH>
                    <TH>Bill</TH>
                    <TH>Dir.</TH>
                    <TH>Route</TH>
                    <TH>Time</TH>
                    <TH>Item</TH>
                    <TH>Article</TH>
                    <TH>Color</TH>
                    <TH>Size</TH>
                    <TH>Barcode</TH>
                    <TH>Sticker</TH>
                    <TH className="text-right">Qty</TH>
                    <TH className="text-right">MRP</TH>
                    <TH>User</TH>
                  </TR>
                </THead>
                <TBody>
                  {transfersQ.data.rows.map((r, i) => (
                    <TR key={`${r.billNo}-${r.rowrollNo}-${i}`}>
                      <TD className="font-medium text-muted-foreground">{storeLabel ?? '—'}</TD>
                      <TD>{fmtDate(r.billDate)}</TD>
                      <TD className="font-mono">{r.billNo}</TD>
                      <TD>
                        <span className={cn(
                          'inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium',
                          r.direction === 'out' ? 'bg-rose-soft text-rose-deep'
                          : r.direction === 'in' ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-muted text-muted-foreground',
                        )}>
                          {r.direction === 'out' ? '↑ OUT' : r.direction === 'in' ? '↓ IN' : '—'}
                        </span>
                      </TD>
                      <TD className="font-mono text-xs text-muted-foreground">{routeLabel(r.src)} → {routeLabel(r.dst)}</TD>
                      <TD className="font-mono-num text-xs text-muted-foreground">{r.transferTime}</TD>
                      <TD>{r.itemName}</TD>
                      <TD>{r.style}</TD>
                      <TD>{r.color}</TD>
                      <TD>{r.size}</TD>
                      <TD className="font-mono text-xs">{r.barcode}</TD>
                      <TD className="font-mono text-xs">{r.rowrollNo}</TD>
                      <TD className="text-right font-mono-num">{r.qty}</TD>
                      <TD className="text-right font-mono-num">{fmtINR(r.mrp)}</TD>
                      <TD className="text-xs text-muted-foreground">{r.posUser}</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>

              <div className="mt-3 flex items-center justify-end gap-2 text-sm">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setF({ page: page - 1 })}>Prev</Button>
                <span className="px-2">Page {page} / {totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setF({ page: page + 1 })}>Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Kpi({
  label, value, hint, accent, icon,
}: {
  label: string; value: string; hint?: string;
  accent?: 'rose' | 'emerald';
  icon?: React.ReactNode;
}) {
  const tone =
    accent === 'rose' ? 'text-rose-deep'
    : accent === 'emerald' ? 'text-emerald-700'
    : 'text-ink';
  return (
    <div className="rounded-lg border bg-background p-3">
      <div className={cn('flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground')}>
        {icon} {label}
      </div>
      <div className={cn('mt-1 font-display text-2xl font-medium leading-tight', tone)}>{value}</div>
      {hint && <div className="font-mono-num text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

function isoToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function isoAdd(iso: string, n: number): string {
  const [y, m, day] = iso.split('-').map(Number);
  const d = new Date(y!, m! - 1, day! + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Map sub-store codes to friendly labels */
function routeLabel(code: string | null | undefined): string {
  if (!code || code === '?') return '?';
  // Sub-store codes within each main store
  const labels: Record<string, string> = {
    '01': 'MG',   // Madangir main
    '02': 'DV',   // Devli
    '03': 'NS',   // Neb Sarai
    'WH': 'WH',   // Warehouse
  };
  return labels[code] ?? code;
}
